import { createContext, useContext, useRef } from "react";
import axios from "axios";
import lodash from "lodash";
import { useSetRecoilState } from "recoil";
import { utils } from "@/comn/utils";
import { COMN_CODE, COMN_CODE_API } from "@/comn/constants";
import { _TEST1234 } from "../config/ApiConfig";
import resourceState from "../recoil/resourceState";
import {
    createResourceNotification,
    createResourceSnapshot,
    getNextResourceVersion,
    makeResourceOptions as makeOptions,
    runResourceRefresh,
} from "./resourceContract";

// const RENEWAL_CYCLE = 10000;
// const RENEWAL_CYCLE = 86400000;
const RENEWAL_CYCLE = 1800000;

const Context = createContext<any>(null);
export const useResourceContext = () => {
    return useContext(Context);
};

class Entry {
    area: any;
    comnCd: any;
    params: any;
    value: any;
    resolve: any;
    reject: any;
    fetchKey: any;
    isExecuted = false;
    promise = new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
    });
    constructor(area: any, comnCd?: any, params?: any, value?: any, fetchKey?: any) {
        this.area = area;
        this.comnCd = comnCd || null;
        this.params = params || null;
        this.value = value || null;
        this.fetchKey = fetchKey || null;
    }
}

const getIdb = (key: any) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open("OSC_RESOURCE");
        request.onupgradeneeded = () => {
            const db = request.result;
            db.createObjectStore("RESOURCE", { keyPath: "key" });
            db.createObjectStore("RESOURCE_REF", { keyPath: "key" });
        };
        request.onsuccess = async () => {
            const db = request.result;
            const ts = db.transaction("RESOURCE", "readonly");
            const os = ts.objectStore("RESOURCE");
            const get = os.get(key);
            get.onsuccess = () => {
                resolve(get.result);
            };
        };
    });
};

const addIdb = (record: any) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open("OSC_RESOURCE");
        request.onupgradeneeded = () => {
            const db = request.result;
            db.createObjectStore("RESOURCE", { keyPath: "key" });
            db.createObjectStore("RESOURCE_REF", { keyPath: "key" });
        };
        request.onsuccess = async () => {
            try {
                const db = request.result;
                const ts = db.transaction(["RESOURCE", "RESOURCE_REF"], "readwrite");
                const os = ts.objectStore("RESOURCE");
                const add = os.add(record);
                add.onsuccess = () => {
                    const { value, ...rest } = record;
                    const os_ref = ts.objectStore("RESOURCE_REF");
                    os_ref.add(rest);
                    // @ts-ignore
                    resolve();
                };
            } catch (error) {
                indexedDB.deleteDatabase("OSC_RESOURCE");
                reject();
            }
        };
    });
};

const putIdb = (record: any) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open("OSC_RESOURCE");
        request.onupgradeneeded = () => {
            const db = request.result;
            db.createObjectStore("RESOURCE", { keyPath: "key" });
            db.createObjectStore("RESOURCE_REF", { keyPath: "key" });
        };
        request.onsuccess = async () => {
            try {
                const db = request.result;
                const ts = db.transaction(["RESOURCE", "RESOURCE_REF"], "readwrite");
                const os = ts.objectStore("RESOURCE");
                const put = os.put(record);
                put.onsuccess = () => {
                    const { value, ...rest } = record;
                    const os_ref = ts.objectStore("RESOURCE_REF");
                    os_ref.put(rest);
                    // @ts-ignore
                    resolve();
                };
            } catch (error) {
                indexedDB.deleteDatabase("OSC_RESOURCE");
                reject();
            }
        };
    });
};

const bulkUpdateIdb = (data: any) => {
    return new Promise<void>((resolve, reject) => {
        const request = indexedDB.open("OSC_RESOURCE");
        request.onupgradeneeded = () => {
            const db = request.result;
            db.createObjectStore("RESOURCE", { keyPath: "key" });
            db.createObjectStore("RESOURCE_REF", { keyPath: "key" });
        };
        request.onsuccess = async () => {
            try {
                const db = request.result;
                const ts = db.transaction(["RESOURCE", "RESOURCE_REF"], "readwrite");
                const os = ts.objectStore("RESOURCE");
                const promises = data.map((item: any) => {
                    return new Promise<void>((resolveItem, rejectItem) => {
                        try {
                            const { key, values, area, comnCd } = item;
                            const get = os.get(key);
                            get.onsuccess = () => {
                                const now = new Date().getTime();
                                if (get.result) {
                                    const record = { ...get.result, value: values, count: values.length, updated: now };
                                    const put = os.put(record);
                                    put.onsuccess = () => {
                                        const { value, ...rest } = record;
                                        const os_ref = ts.objectStore("RESOURCE_REF");
                                        os_ref.put(rest);
                                        resolveItem();
                                    };
                                } else {
                                    const record = {
                                        key,
                                        value: values,
                                        count: values.length,
                                        created: now,
                                        updated: now,
                                        area,
                                        comnCd,
                                    };
                                    const add = os.add(record);
                                    add.onsuccess = () => {
                                        const { value, ...rest } = record;
                                        const os_ref = ts.objectStore("RESOURCE_REF");
                                        os_ref.add(rest);
                                        resolveItem();
                                    };
                                }
                            };
                        } catch (error) {
                            rejectItem();
                        }
                    });
                });
                await Promise.allSettled(promises);
                resolve();
            } catch (error) {
                // indexedDB.deleteDatabase("OSC_RESOURCE");
                reject();
            }
        };
    });
};

const updateIdb = (key: any, value: any, area: any, comnCd: any) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open("OSC_RESOURCE");
        request.onupgradeneeded = () => {
            const db = request.result;
            db.createObjectStore("RESOURCE", { keyPath: "key" });
            db.createObjectStore("RESOURCE_REF", { keyPath: "key" });
        };
        request.onsuccess = async () => {
            try {
                const db = request.result;
                const ts = db.transaction(["RESOURCE", "RESOURCE_REF"], "readwrite");
                const os = ts.objectStore("RESOURCE");
                const get = os.get(key);
                get.onsuccess = () => {
                    const now = getNextResourceVersion(get.result?.updated);
                    if (get.result) {
                        const record = { ...get.result, value, count: value.length, updated: now };
                        const put = os.put(record);
                        put.onsuccess = () => {
                            const { value, ...rest } = record;
                            const os_ref = ts.objectStore("RESOURCE_REF");
                            os_ref.put(rest);
                            resolve(record);
                        };
                    } else {
                        const record = { key, value, count: value.length, created: now, updated: now, area, comnCd };
                        const add = os.add(record);
                        add.onsuccess = () => {
                            const { value, ...rest } = record;
                            const os_ref = ts.objectStore("RESOURCE_REF");
                            os_ref.add(rest);
                            resolve(record);
                        };
                    }
                };
            } catch (error) {
                indexedDB.deleteDatabase("OSC_RESOURCE");
                reject();
            }
        };
    });
};

const fetch = async (area: any, comnCd?: any) => {
    const { data } = await utils.getCode({ area, comnCd });
    const result = data[Object.keys(data)[0]].content || [];

    return result;
};

const fetchSingle = async (area: any, comnCd?: any, params?: any, value?: any) => {
    const code = COMN_CODE[area];
    const queryString = (code.path_async.includes("?") ? "&" : "?") + `${code.value}=${value}`;
    const url = COMN_CODE_API + code.path_async + queryString;
    const config: any = { headers: {} };
    if (params) config.params = utils.toGetParams(params);
    config.headers["Accept-Language"] = _TEST1234.LANGUAGE;

    const { data } = await axios.get(url, config);
    const result = data[Object.keys(data)[0]].content || [];

    return result;
};
const fetchMultiple = async (area: any, comnCd?: any, params?: any, values?: any[]) => {
    const code = COMN_CODE[area];
    const url = COMN_CODE_API + code.path_async;
    const config: any = { headers: {} };
    if (params) config.params = utils.toGetParams(params);
    config.headers["Accept-Language"] = _TEST1234.LANGUAGE;

    const { data } = await axios.post(url, { [code.value + "s"]: values }, config);
    const result = data[Object.keys(data)[0]].content || [];
    return result;
};

const makeFetcher = ($: any, fetchKey: any, area: any, comnCd?: any, params?: any) =>
    lodash.debounce(async () => {
        const entries = Object.entries($.current.matchesQueue)
            .filter(([, item]: any) => {
                return !item.isExecuted && item.fetchKey === fetchKey;
            })
            .map(([key, item]) => {
                $.current.matchesQueue[key].fetched = true;
                return item;
            });

        try {
            const code = COMN_CODE[area];
            const result = await fetchMultiple(
                area,
                comnCd,
                params,
                entries.map(({ value }: any) => value),
            );
            entries.forEach(({ value, resolve }: any) => {
                const key = getKey(area, comnCd, params, value);
                const matched = result.filter((item: any) => {
                    return item[code.value] === value;
                });
                const match: any = {};
                match.key = key;
                match.data = matched;
                match.count = matched.length;
                match.options = matched.map((item: any) => {
                    return { value: item[code.value], label: item[code.label] };
                });

                $.current.matches[key] = match;
                delete $.current.matchesQueue[key];
                resolve(match);
            });

            // Fetch resources by values
            // Resolve each entry & delete from queue

            // // Delete fetcher
            // delete $.current.fetchers[fetchKey];
        } catch (error) {
            entries.forEach(({ reject }: any) => {
                reject();
            });
            // console.log(error);
            // console.log(entries);
            // console.log(error);
            // delete $.current.fetchers[fetchKey];
            // targets.forEach(({ resourceKey }: any) => {
            //     delete $.current.queue[resourceKey];
            // });
        }
    }, 500);

const ResourceContextProvider = ({ children }: any) => {
    const setResourceNotification = useSetRecoilState(resourceState);
    const $ = useRef<any>({
        queue: {},
        resources: {},
        resourceRefs: {},
        fetchers: {},
        matches: {},
        matchesQueue: {},
        refreshes: new Map<string, Promise<any>>(),
    });

    const getResources = () => {
        return $.current.resources;
    };

    const getResourceRefs = () => {
        return $.current.resourceRefs;
    };

    const getResource = (area: any, comnCd?: any, params?: any, value?: any) => {
        return $.current.resources[getKey(area, comnCd, params, value)];
    };

    const updateResource = async (area: any, comnCd?: any) => {
        const key = getKey(area, comnCd);
        return runResourceRefresh($.current.refreshes, key, async () => {
            try {
                // 초기 적재와 갱신이 겹치면 초기 응답이 최신 데이터를 덮지 않도록 적재 완료 후 갱신한다.
                const initialEntry = $.current.queue[key];
                if (initialEntry && !$.current.resources[key]) {
                    try {
                        await initialEntry.promise;
                    } catch (error) {
                        // 초기 적재 실패는 강제 갱신에서 다시 조회한다.
                    }
                }

                const result = await fetch(area, comnCd);
                const record: any = await updateIdb(key, result, area, comnCd);
                const newResource = createResourceSnapshot(area, comnCd, record);

                $.current.resources[key] = newResource;
                $.current.resourceRefs[key] = { key, area, comnCd };
                setResourceNotification((prev: any) => ({
                    ...prev,
                    [key]: createResourceNotification(newResource),
                }));
                return newResource;
            } catch (error) {
                console.log(error);
                return $.current.resources[key] || null;
            }
        });
    };

    const insertNormal = async (area: any, comnCd?: any, params?: any) => {
        const key = getKey(area, comnCd, params);
        try {
            const code = COMN_CODE[area];
            // Insert entry
            $.current.queue[key] = new Entry(area, comnCd, params);

            const newResource: any = {};
            const now = new Date();
            const idb: any = await getIdb(key);

            if (!idb || now.getTime() - new Date(idb.updated).getTime() > RENEWAL_CYCLE) {
                const data = await fetch(area, comnCd);
                idb
                    ? putIdb({ ...idb, value: data, count: data.length, updated: now.getTime() })
                    : addIdb({
                          key,
                          value: data,
                          count: data.length,
                          created: now.getTime(),
                          updated: now.getTime(),
                          area,
                          comnCd,
                      });
                newResource.data = data;
                newResource.updated = now.getTime();
            } else {
                newResource.data = idb.value;
                newResource.updated = idb.updated;
            }

            newResource.key = key;
            newResource.area = area;
            newResource.comnCd = comnCd;
            newResource.count = newResource.data.length;
            newResource.options = makeOptions(area, newResource.data);
            $.current.resources[key] = newResource;
            $.current.resourceRefs[key] = { key, area, comnCd };
            setResourceNotification((prev: any) => ({
                ...prev,
                [key]: createResourceNotification(newResource),
            }));
            // Resolve
            $.current.queue[key].resolve(newResource);
        } catch (error) {
            console.log(error);
            $.current.queue[key]?.reject(error);
            delete $.current.queue[key];
        }
    };

    const insertSingle = async (area: any, comnCd?: any, params?: any, value?: any) => {
        const code = COMN_CODE[area];
        const key = getKey(area, comnCd, params, value);
        try {
            // Insert
            $.current.queue[key] = new Entry(area, undefined, params, value);
            const newResource: any = {};
            newResource.key = key;
            // Execute
            newResource.data = await fetchSingle(area, undefined, params, value);
            newResource.count = newResource.data.length;
            newResource.options = makeOptions(area, newResource.data);
            $.current.resources[key] = newResource;
            // Resolve
            $.current.queue[key].resolve(newResource);
        } catch (error) {
            console.log(error);
            $.current.queue[key].reject();
            delete $.current.queue[key];
            // throw error;
        }
    };

    const asyncNormal = (area: any, comnCd?: any, params?: any) => {
        const code = COMN_CODE[area];
        if (!code || code.async) {
            throw new Error();
        }

        const key = getKey(area, comnCd, params);

        // Check the data
        const resource = getResource(area, comnCd, params);
        if (resource) return resource;

        // 갱신 중 새로 마운트된 소비자는 별도 초기 조회 대신 같은 완료 Promise를 공유한다.
        const refresh = $.current.refreshes.get(key);
        if (refresh) return refresh;

        // Check the queue
        const entry = $.current.queue[key];
        if (!entry) insertNormal(area, comnCd, params);

        // Return the entry
        return $.current.queue[key].promise;
    };

    const asyncSingle = (area: any, comnCd?: any, params?: any, value?: any) => {
        const code = COMN_CODE[area];
        if (!value || !code || !code.async || value.length < code.path2MinLength) {
            throw new Error();
        }

        const key = getKey(area, undefined, params, value);

        // Check the data
        const resource = getResource(area, undefined, params, value);
        if (resource) return resource;

        // Check the queue
        const entry = $.current.queue[key];
        if (!entry) insertSingle(area, comnCd, params, value);

        // Return the entry
        return $.current.queue[key].promise;
    };

    const asyncMultiple = (area: any, comnCd?: any, params?: any, value?: any) => {
        const code = COMN_CODE[area];
        if (!value || !code || !code.async || value.length < code.path2MinLength) {
            throw new Error();
        }

        const key = getKey(area, undefined, params, value);

        // Check the data
        const match = $.current.matches[key];
        if (match) return match;

        const fetchKey = getKey(area, undefined, params, undefined);
        if (!$.current.fetchers[fetchKey]) {
            $.current.fetchers[fetchKey] = makeFetcher($, fetchKey, area, comnCd, params);
        }

        // Check the queue
        const entry = $.current.matchesQueue[key];
        if (!entry) {
            $.current.matchesQueue[key] = new Entry(area, comnCd, params, value, fetchKey);
            $.current.fetchers[fetchKey]();
        }

        return $.current.matchesQueue[key].promise;
    };

    return (
        <Context.Provider
            value={{
                makeOptions,
                makeOption,
                findMatchData,
                getIsAsync,
                getKey,
                getResource,
                getResources,
                getResourceRefs,
                updateResource,
                asyncNormal,
                asyncSingle,
                asyncMultiple,
                bulkUpdateIdb,
            }}
        >
            {children}
        </Context.Provider>
    );
};

const ResourceContext = {
    Provider: ResourceContextProvider,
};

export default ResourceContext;

const getIsAsync = (area: any) => {
    return !!COMN_CODE[area]?.async;
};

const getKey = (area: any, comnCd?: any, params?: any, value?: any) => {
    const comnCdKey = comnCd ? `:${comnCd}` : "";
    const languageKey = `;${_TEST1234.LANGUAGE}`.toLowerCase();
    const valueKey = value ? `;${value}` : "";
    const paramsKey = params ? `;${JSON.stringify(params)}` : "";
    return `${area}${comnCdKey}${languageKey}${paramsKey}${valueKey}`;
};

const makeOption = (area: any, item: any) => {
    const code = COMN_CODE[area];
    const option: any = { value: undefined, label: undefined };

    if (code) {
        option.value = item[code.value];
        let label = "";
        if (Array.isArray(code.label)) {
            label = code.label
                .map((_: any) => {
                    return item[_];
                })
                .join(", ");
        } else label = item[code.label];
        option.label = label;
    }

    return option;
};

const findMatchData = (area: any, option: any, data: any[]) => {
    const code = COMN_CODE[area];
    if (code) {
        return data.find((item: any) => {
            return item?.[code.value] === option?.value;
        });
    } else {
        return option;
    }
};
